import { RestGetAllResponse } from '@/certperm/infrastructure/rest/types/RestGetAllResponse';
import axiosServices from '@/common/infrastructure/axios';
import { AxiosError } from 'axios';
import { extractRestApiErrorMessage } from '@/common/application/utils/restApiErrorUtils';
import type { RaptorJobDto, CreateSingleRaptorJobInput, UpdateRaptorJobInput } from '@/libs/api-rest/generated/model';

class RaptorJobApiService {
  private getBaseUrl(): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    if (!baseUrl) {
      throw new Error('VITE_API_BASE_URL environment variable is required');
    }
    return baseUrl;
  }

  /**
   * Get all Raptor Jobs with pagination, sorting, and filtering
   * params should be a url-encoded query string (e.g., Page=1&PageSize=25&Filters=Name@%3Dtest)
   */
  async getRaptorJobs(params: string): Promise<RestGetAllResponse<RaptorJobDto>> {
    try {
      const response = await axiosServices.get(`${this.getBaseUrl()}/api/v3/raptor-jobs${params ? `?${params}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch raptor jobs:', error);
      throw new Error(`Failed to fetch raptor jobs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get a single Raptor Job by ID
   */
  async getRaptorJob(id: number): Promise<RaptorJobDto> {
    try {
      const response = await axiosServices.get(`${this.getBaseUrl()}/api/v3/raptor-jobs/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch raptor job ${id}:`, error);
      throw new Error(`Failed to fetch raptor job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create a new Raptor Job
   */
  async createRaptorJob(data: CreateSingleRaptorJobInput): Promise<RaptorJobDto> {
    try {
      const response = await axiosServices.post(`${this.getBaseUrl()}/api/v3/raptor-jobs`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create raptor job:', error);
      throw new Error(`Failed to create raptor job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Cancel a Raptor Job
   */
  async cancelRaptorJob(id: number, legacy: boolean = true): Promise<RaptorJobDto> {
    try {
      const queryParams = new URLSearchParams({ ...(legacy ? { legacy: 'true' } : {}) }).toString();
      const response = await axiosServices.post(
        `${this.getBaseUrl()}/api/v3/raptor-jobs/${id}/cancel${queryParams ? `?${queryParams}` : ''}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to cancel raptor job ${id}:`, error);

      // Extract descriptive error message from backend response
      if (error instanceof AxiosError && error.response?.data) {
        const errorMessage = extractRestApiErrorMessage(error.response.data, 'Failed to cancel raptor job');
        throw new Error(errorMessage);
      }

      throw new Error(`Failed to cancel raptor job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create a single-day Raptor Job (with resources, estimates, and custom fields)
   */
  async createSingleDayJob(data: CreateSingleRaptorJobInput): Promise<RaptorJobDto> {
    try {
      const response = await axiosServices.post(`${this.getBaseUrl()}/api/v3/raptor-jobs/create-single-day-job`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create single-day raptor job:', error);
      throw new Error(`Failed to create single-day raptor job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update an existing Raptor Job
   */
  async updateRaptorJob(id: number, data: UpdateRaptorJobInput & { legacy?: boolean }): Promise<RaptorJobDto> {
    try {
      // Add legacy=true query parameter if legacy flag is present and true
      const queryParams = new URLSearchParams({ ...(data.legacy === true ? { legacy: 'true' } : {}) }).toString();
      const response = await axiosServices.put(
        `${this.getBaseUrl()}/api/v3/raptor-jobs/${id}${queryParams ? `?${queryParams}` : ''}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to update raptor job ${id}:`, error);
      const message = `Failed to update raptor job: ${error instanceof Error ? error.message : 'Unknown error'}`;
      const err = new Error(message) as Error & { responseData?: unknown };
      if (error instanceof AxiosError && error.response?.data != null) {
        err.responseData = error.response.data;
      }
      throw err;
    }
  }
}

export const raptorJobApiService = new RaptorJobApiService();
